import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Layout, Model, TabNode, Actions, Action, DockLocation, IJsonModel, IJsonBorderNode, ITabRenderValues } from 'flexlayout-react'
import { TabsContext, TabSpec, SaveResult, ANY_RESOURCE } from './TabsContext'
import { useAuth } from './AuthContext'
import { getSocket } from '../socket'
import Dashboard from '../pages/Dashboard'
import CustomersList from '../pages/CustomersList'
import CustomerForm from '../pages/CustomerForm'
import InvoicesList from '../pages/InvoicesList'
import InvoiceForm from '../pages/InvoiceForm'
import Templates, { type TemplatesPill } from '../pages/Templates'
import InvoiceTemplateEditor from '../pages/InvoiceTemplateEditor'
import CertificateDispatchList from '../pages/CertificateDispatchList'
import SubscriptionsList from '../pages/SubscriptionsList'
import SubscriptionForm from '../pages/SubscriptionForm'
import MonthlyExport from '../pages/MonthlyExport'
import Settings from '../pages/Settings'
import VatSettings from '../pages/VatSettings'
import WebhooksManager from '../pages/WebhooksManager'
import LogsViewer from '../pages/LogsViewer'
import TransactionsList from '../pages/TransactionsList'
import TrashView from '../pages/TrashView'
import UsersList from '../pages/UsersList'
import UserForm from '../pages/UserForm'
import RolesList from '../pages/RolesList'
import RoleEditor from '../pages/RoleEditor'
import ExpenseMappingAdmin from '../pages/ExpenseMappingAdmin'
import AuthorCatalog from '../pages/AuthorCatalog'
import CatalogItemForm from '../pages/CatalogItemForm'
import AuthorsList from '../pages/AuthorsList'
import AuthorForm from '../pages/AuthorForm'
import AuthorStatements from '../pages/AuthorStatements'
import Profile from '../pages/Profile'
import FileViewer from '../pages/FileViewer'
import FullscreenButton from '../components/FullscreenButton'
import { BACK_GUARD_REARM_MS, MAX_TAB_TITLE_LENGTH } from '../../shared/constants'
import { fill, LAYOUT_LAYOUT_MANAGER_TEXTS as T } from '../../shared/texts'

const LS_KEY = 'bo-layout'

// flexlayout 0.9.1 remembers the edge a tab drag last came within 10 px of (showHiddenBorder) and,
// on the next DELETE_TAB, looks up the border at that edge — without checking that one exists. With
// no borders that lookup is undefined and every later tab close threw (operator, 31.10.2026, Safari:
// „undefined is not an object (… getBorderMap().get(this._state.showHiddenBorder).getChildren')",
// right after „Entwurf anlegen"). So the model always carries the four borders, HIDDEN: show:false
// keeps them out of the drawing and out of the drop targets, nothing changes on screen.
const HIDDEN_BORDERS: IJsonBorderNode[] = (['top', 'bottom', 'left', 'right'] as const)
    .map((location) => ({ type: 'border', location, show: false, children: [] }))

// A saved layout from before the fix has no borders (or not all four) — add the missing ones.
function withHiddenBorders(json: IJsonModel): IJsonModel {
    const have = new Set((json.borders ?? []).map((b) => b.location))
    return { ...json, borders: [...(json.borders ?? []), ...HIDDEN_BORDERS.filter((b) => !have.has(b.location))] }
}

// One always-present tabset ('main', never auto-deleted) so there is always a drop
// target; dragging tabs to edges creates further tabsets (side-by-side / stacked).
const DEFAULT_LAYOUT: IJsonModel = {
    global: { tabEnableClose: true, tabEnableRename: false, tabSetEnableMaximize: true },
    borders: HIDDEN_BORDERS,
    layout: {
        type: 'row',
        weight: 100,
        children: [
            {
                type: 'tabset', id: 'main', weight: 100, enableDeleteWhenEmpty: false,
                children: [{ type: 'tab', id: 'dashboard', name: T.nav.dashboard, component: 'dashboard' }],
            },
        ],
    },
}

function loadModel(): Model {
    try {
        const saved = localStorage.getItem(LS_KEY)
        if (saved) return Model.fromJson(withHiddenBorders(JSON.parse(saved)))
    } catch { /* corrupt → fall back to default */ }
    return Model.fromJson(DEFAULT_LAYOUT)
}

// Longest tab title shown. Beyond this the tab crowds out its neighbours; the full name
// remains available as a tooltip.
const MAX_TAB_TITLE = MAX_TAB_TITLE_LENGTH

// How many tabs are open across all tabsets. 0 → show the empty-state page.
function countTabs(model: Model): number {
    let n = 0
    model.visitNodes((node) => { if (node.getType() === 'tab') n++ })
    return n
}

// Sidebar items carry the module they belong to; an item is shown only when the
// user's role has read on that module (RBAC). Management modules resolve to admin.
// Entries are either a single item or a collapsible group of items.
// `module`: the rights module a role must be able to READ. An array means ANY of them — the page
// System → Vorlagen holds the PDF templates (invoices) and the mail templates (settings), and
// shows each pill only for its own module (pages/Templates.tsx).
type NavItem = TabSpec & { module: string | string[] }
type NavGroup = { id: string, name: string, children: NavItem[] }
type NavEntry = NavItem | NavGroup
const isGroup = (e: NavEntry): e is NavGroup => 'children' in e
const canReadAny = (can: (m: string, c: 'read') => boolean, mod: string | string[]) =>
    (Array.isArray(mod) ? mod : [mod]).some((m) => can(m, 'read'))

const SIDEBAR: NavEntry[] = [
    { id: 'dashboard', name: T.nav.dashboard, component: 'dashboard', module: 'dashboard' },
    { id: 'customers', name: T.nav.customers, component: 'customers', module: 'customers' },
    // Eigener Punkt auf oberster Ebene, KEINE neue Rubrik. Der Betreiber hat eine Rubrik fuer
    // solche Module ueberlegt - richtig gedacht, aber das beruehrt jede Seite und soll nicht
    // Nebenwirkung dieses Moduls sein. Kommt ein zweites dieser Art, weiss man auch, wie die
    // Rubrik heissen muss.
    { id: 'certificate-dispatch', name: T.nav.certificateDispatch, component: 'certificate-dispatch', module: 'certificates' },
    {
        id: 'finance', name: T.nav.finance, children: [
            { id: 'invoices', name: T.nav.invoices, component: 'invoices', module: 'invoices' },
            { id: 'subscriptions', name: T.nav.subscriptions, component: 'subscriptions', module: 'subscriptions' },
            { id: 'transactions', name: T.nav.transactions, component: 'transactions', module: 'transactions' },
            { id: 'export', name: T.nav.export, component: 'export', module: 'export' },
            { id: 'expense-mapping', name: T.nav.expenseMapping, component: 'expense-mapping', module: 'expense-mapping' },
            { id: 'catalog', name: T.nav.catalog, component: 'catalog', module: 'invoices' },
            { id: 'authors', name: T.nav.authors, component: 'authors', module: 'invoices' },
            { id: 'author-statements', name: T.nav.authorStatements, component: 'author-statements', module: 'invoices' },
            { id: 'vat-settings', name: T.nav.vatSettings, component: 'vat-settings', module: 'settings' },
        ],
    },
    {
        id: 'system', name: T.nav.system, children: [
            { id: 'users', name: T.nav.users, component: 'users', module: 'users' },
            { id: 'roles', name: T.nav.roles, component: 'roles', module: 'roles' },
            { id: 'settings', name: T.nav.settings, component: 'settings', module: 'settings' },
            // Alle Vorlagen an einem Ort (Betreiber, 27.09.2026): Pill PDF (bis dahin Finanzen →
            // PDF-Vorlagen) und Pill E-Mail (Mailtexte und Mail-Rahmen, bis dahin in Einstellungen).
            { id: 'templates', name: T.nav.templates, component: 'templates', module: ['invoices', 'settings'] },
            { id: 'webhooks', name: T.nav.webhooks, component: 'webhooks', module: 'webhooks' },
            { id: 'logs', name: T.nav.logs, component: 'logs', module: 'logs' },
            { id: 'trash', name: T.nav.trash, component: 'trash', module: 'trash' },
        ],
    },
]

// Which module a tab component belongs to (detail forms share their list's module).
const COMPONENT_MODULE: Record<string, string | string[]> = {
    dashboard: 'dashboard',
    customers: 'customers', 'customer-form': 'customers',
    invoices: 'invoices', 'invoice-form': 'invoices', 'invoice-template-form': 'invoices',
    // ⚠️ 'invoice-templates' stays: it is the component of the old Finanzen → PDF-Vorlagen tab and
    // sits in saved tab layouts. It now opens System → Vorlagen on the PDF pill instead of running
    // into nothing (see the factory below).
    templates: ['invoices', 'settings'], 'invoice-templates': ['invoices', 'settings'],
    subscriptions: 'subscriptions', 'subscription-form': 'subscriptions',
    transactions: 'transactions',
    export: 'export',
    settings: 'settings',
    users: 'users', 'user-form': 'users',
    roles: 'roles', 'role-editor': 'roles',
    'expense-mapping': 'expense-mapping',
    'vat-settings': 'settings',
    'certificate-dispatch': 'certificates',
    'catalog-item-form': 'invoices',
    'author-form': 'invoices',
    webhooks: 'webhooks',
    logs: 'logs',
    trash: 'trash',
}

// Inline SVGs (lucide-style, 24×24, stroke=currentColor) keyed by sidebar id.
// The label is shown only as a tooltip (title attr) / aria-label.
const svg = (children: ReactNode): ReactNode => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
)
// Maps a tab's component to the sidebar icon shown next to its title (detail
// forms reuse their list's icon, e.g. customer-form → customers).
const COMPONENT_ICON: Record<string, string> = {
    dashboard: 'dashboard',
    customers: 'customers', 'customer-form': 'customers',
    invoices: 'invoices', 'invoice-form': 'invoices', templates: 'templates', 'invoice-templates': 'templates', 'invoice-template-form': 'templates',
    subscriptions: 'subscriptions', 'subscription-form': 'subscriptions',
    transactions: 'transactions',
    export: 'export',
    settings: 'settings',
    webhooks: 'webhooks',
    logs: 'logs',
    'file-viewer': 'pdf',
    trash: 'trash',
    users: 'users', 'user-form': 'users',
    roles: 'roles', 'role-editor': 'roles',
    'expense-mapping': 'expense-mapping',
    'vat-settings': 'vat-settings',
    'certificate-dispatch': 'certificate-dispatch',
    catalog: 'catalog', 'catalog-item-form': 'catalog',
    authors: 'authors', 'author-form': 'authors',
    'author-statements': 'author-statements',
}
const ICONS: Record<string, ReactNode> = {
    dashboard: svg(<><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></>),
    customers: svg(<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>),
    invoices: svg(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><line x1="10" y1="9" x2="8" y2="9" /></>),
    templates: svg(<><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="12" y1="3" x2="12" y2="21" /><line x1="6" y1="8" x2="9" y2="8" /><line x1="6" y1="12" x2="9" y2="12" /></>),
    // Umschlag - es geht um Post, nicht um die Urkunde. Wer hier hinklickt, will wissen, was
    // noch wegzubringen ist.
    'certificate-dispatch': svg(<><rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="2,6 12,13 22,6" /></>),
    subscriptions: svg(<><polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 0 1 4-4h14" /><polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 0 1-4 4H3" /></>),
    export: svg(<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></>),
    settings: svg(<><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></>),
    trash: svg(<><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></>),
    users: svg(<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>),
    roles: svg(<><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" /></>),
    'expense-mapping': svg(<><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></>),
    'vat-settings': svg(<><line x1="19" y1="5" x2="5" y2="19" /><circle cx="6.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="17.5" r="2.5" /></>),
    webhooks: svg(<><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" /></>),
    // Stapel aus Kursen/Aufzeichnungen — der verkaufbare Artikel.
    catalog: svg(<><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /><line x1="9" y1="7" x2="16" y2="7" /></>),
    // Person mit Stift — wer die Inhalte schreibt.
    'author-statements': svg(<><path d="M12 1v22" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></>),
    authors: svg(<><path d="M14 19a4 4 0 0 0-8 0" /><circle cx="10" cy="9" r="3" /><path d="M17 3.5a2.12 2.12 0 0 1 3 3L15.5 11 13 11.5l.5-2.5z" /></>),
    // Karte mit Betrag-Linie — Zahlungen beim PSP.
    transactions: svg(<><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /><line x1="6" y1="15" x2="10" y2="15" /></>),
    logs: svg(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><line x1="10" y1="9" x2="8" y2="9" /></>),
    pdf: svg(<><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></>),
    // Group icons.
    finance: svg(<><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" /><path d="M3 5v14a2 2 0 0 0 2 2h16v-5" /><path d="M18 12a2 2 0 0 0 0 4h4v-4Z" /></>),
    system: svg(<><rect x="2" y="2" width="20" height="8" rx="2" /><rect x="2" y="14" width="20" height="8" rx="2" /><line x1="6" y1="6" x2="6.01" y2="6" /><line x1="6" y1="18" x2="6.01" y2="18" /></>),
}
// Chevron shown on a group header (rotates when the group is open).
const CARET = svg(<polyline points="6 9 12 15 18 9" />)

export default function LayoutManager() {
    const modelRef = useRef<Model>(loadModel())
    const model = modelRef.current
    const [versions, setVersions] = useState<Record<string, number>>({})
    const [empty, setEmpty] = useState(() => countTabs(model) === 0)
    const [menuOpen, setMenuOpen] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)
    // Which sidebar groups are expanded (persisted; both open by default).
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
        try { return { finance: true, system: true, ...JSON.parse(localStorage.getItem('sidebar.groups') || '{}') } }
        catch { return { finance: true, system: true } }
    })
    const toggleGroup = useCallback((id: string) => {
        setOpenGroups((g) => {
            const next = { ...g, [id]: !(g[id] ?? true) }
            try { localStorage.setItem('sidebar.groups', JSON.stringify(next)) } catch { /* ignore */ }
            return next
        })
    }, [])
    const { user, logout, can } = useAuth()
    // Kept in a ref so the flexlayout factory can gate by module without being
    // re-created (which would remount tabs) on every auth change.
    const canRef = useRef(can)
    canRef.current = can

    // Detail forms register {dirty, save} here (keyed by tab id). dirtyTick forces
    // the tab strip to re-render its ● markers when a form's dirty state flips.
    const guards = useRef(new Map<string, { dirty: boolean, save: () => Promise<SaveResult> }>())
    const [dirtyTick, setDirtyTick] = useState(0)
    // Close-confirm dialog target (tab id) + in-flight save flag + save error.
    const [confirm, setConfirm] = useState<string | null>(null)
    const [confirmBusy, setConfirmBusy] = useState(false)
    const [confirmError, setConfirmError] = useState('')
    // Exit-confirm dialog + the topbar's "n ungespeichert" popover. Both drive the
    // same saveAllDirty() below. `kind` only picks the wording; `proceed` is what
    // the user was trying to do, run once nothing is left unsaved — so every
    // interceptable way out of the app shares ONE dialog instead of growing its own.
    const [exitPrompt, setExitPrompt] = useState<{ kind: 'reload' | 'back' | 'logout', proceed: () => void } | null>(null)
    // Set while we ourselves are driving a history traversal, so the popstate
    // guard below doesn't mistake it for the user pressing Back again.
    const leaving = useRef(false)
    const [bulkBusy, setBulkBusy] = useState(false)
    const [bulkErrors, setBulkErrors] = useState<string[]>([])
    const [dirtyListOpen, setDirtyListOpen] = useState(false)
    const dirtyRef = useRef<HTMLDivElement>(null)

    const registerDirtyGuard = useCallback((tabId: string, dirty: boolean, save: () => Promise<SaveResult>) => {
        guards.current.set(tabId, { dirty, save })
        setDirtyTick((t) => t + 1)
    }, [])
    const unregisterDirtyGuard = useCallback((tabId: string) => {
        guards.current.delete(tabId)
        setDirtyTick((t) => t + 1)
    }, [])

    // Warn on window/app close if any tab has unsaved changes (native browser
    // prompt — browsers don't allow a custom save dialog here).
    useEffect(() => {
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            if ([...guards.current.values()].some((g) => g.dirty)) { e.preventDefault(); e.returnValue = '' }
        }
        window.addEventListener('beforeunload', onBeforeUnload)
        return () => window.removeEventListener('beforeunload', onBeforeUnload)
    }, [])

    // The dirty tabs BY NAME. guards is keyed by tab id and knows nothing about
    // titles; the flexlayout node has the title but not the dirty state. This is
    // the one place that sees both, so the join happens here and nowhere else —
    // a second copy of "which tabs are dirty" would drift from the ● markers.
    // dirtyTick is the same trigger that re-renders those markers.
    const dirtyTabs = useMemo(() => {
        const out: { id: string, name: string }[] = []
        for (const [id, g] of guards.current) {
            if (!g.dirty) continue
            const node = model.getNodeById(id) as TabNode | undefined
            // A guard whose tab is gone from the model is stale — skip it rather
            // than naming a tab the user cannot see.
            if (!node) continue
            out.push({ id, name: node.getName() || id })
        }
        return out
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [model, dirtyTick])

    // Save every dirty tab, one after the other, and report which ones failed.
    // Deliberately does NOT reload or close anything — the callers decide that,
    // and they only do it when this comes back empty.
    const saveAllDirty = useCallback(async (): Promise<string[]> => {
        const errors: string[] = []
        for (const { id, name } of dirtyTabs) {
            const g = guards.current.get(id)
            if (!g) continue
            const res = await g.save().catch(() => ({ ok: false } as SaveResult))
            if (res.ok) guards.current.set(id, { ...g, dirty: false })
            else errors.push(fill(T.saveError, { name, error: res.error ?? T.saveFailed }))
        }
        setDirtyTick((t) => t + 1)
        return errors
    }, [dirtyTabs])

    // Dialog: save everything, then do what the user was trying to do — but ONLY
    // if every save worked. Carrying on after a failed save would destroy exactly
    // the data we were asked to rescue, so we stop and show the error instead.
    const saveAllAndProceed = useCallback(async () => {
        if (!exitPrompt) return
        setBulkBusy(true); setBulkErrors([])
        const errors = await saveAllDirty()
        setBulkBusy(false)
        if (errors.length) { setBulkErrors(errors); return }
        exitPrompt.proceed()
    }, [exitPrompt, saveAllDirty])

    // Dialog: drop the changes and carry on. Clearing the guards first keeps the
    // native beforeunload prompt from asking a second time about a decision the
    // user just made here.
    const discardAndProceed = useCallback(() => {
        if (!exitPrompt) return
        for (const [id, g] of guards.current) guards.current.set(id, { ...g, dirty: false })
        const { proceed } = exitPrompt
        setExitPrompt(null)
        proceed()
    }, [exitPrompt])

    // Signing out drops the session and everything unsaved with it — and the native
    // prompt never sees it, because the page is not being left at all. Same dialog
    // as every other way out.
    const requestLogout = useCallback(() => {
        if (![...guards.current.values()].some((g) => g.dirty)) { void logout(); return }
        setBulkErrors([])
        setExitPrompt({ kind: 'logout', proceed: () => { void logout() } })
    }, [logout])

    // Topbar popover: save everything without reloading.
    const saveAllFromList = useCallback(async () => {
        setBulkBusy(true); setBulkErrors([])
        const errors = await saveAllDirty()
        setBulkBusy(false)
        setBulkErrors(errors)
        if (!errors.length) setDirtyListOpen(false)
    }, [saveAllDirty])

    // Ctrl+R / Cmd+R / F5 (and the Shift variants, which reload just as hard):
    // swallow the keystroke and ask by name — but ONLY while something is dirty.
    // With a clean board the key must reload normally; a refresh blocker that
    // always fires would be worse than the problem it solves.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const reloadKey = e.key === 'F5' || ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'r')
            if (!reloadKey) return
            if (![...guards.current.values()].some((g) => g.dirty)) return
            e.preventDefault()
            setBulkErrors([])
            setExitPrompt({ kind: 'reload', proceed: () => window.location.reload() })
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [])

    // Back / Alt+← / the trackpad swipe. Measured, not assumed: the entry before
    // us is a DIFFERENT document, so popstate never fires — the document is torn
    // down and only the native prompt could intervene. The one way to get a say is
    // to leave a same-document entry of our own behind, so the first Back lands
    // here instead of leaving.
    useEffect(() => {
        // ...but only where Back actually goes somewhere. In a tab opened straight
        // on the app (bookmark, new tab) there is nothing behind us: an anchor
        // would INVENT a Back that pops a dialog and then leads nowhere. Read the
        // depth before adding our own entry.
        if (history.length <= 1) return
        const arm = () => history.pushState({ boGuard: true }, '', location.href)
        arm()
        const onPop = () => {
            if (leaving.current) return
            if (![...guards.current.values()].some((g) => g.dirty)) {
                // Nothing to protect: honour the Back they actually pressed. We are
                // on the entry before the anchor now, so one more step goes where
                // they wanted. Re-arm in case that step found nothing and we stayed.
                leaving.current = true
                history.back()
                window.setTimeout(() => { leaving.current = false; arm() }, BACK_GUARD_REARM_MS)
                return
            }
            arm() // put the anchor back, so cancelling leaves the guard in place
            setBulkErrors([])
            setExitPrompt({ kind: 'back', proceed: () => { leaving.current = true; history.go(-2) } })
        }
        window.addEventListener('popstate', onPop)
        return () => window.removeEventListener('popstate', onPop)
    }, [])

    // Close the unsaved-tabs popover on outside click / Escape (same handling as
    // the profile menu above).
    useEffect(() => {
        if (!dirtyListOpen) return
        const onDown = (e: MouseEvent) => {
            if (dirtyRef.current && !dirtyRef.current.contains(e.target as Node)) setDirtyListOpen(false)
        }
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDirtyListOpen(false) }
        document.addEventListener('mousedown', onDown)
        document.addEventListener('keydown', onKey)
        return () => {
            document.removeEventListener('mousedown', onDown)
            document.removeEventListener('keydown', onKey)
        }
    }, [dirtyListOpen])

    // Close the profile menu on outside click / Escape.
    useEffect(() => {
        if (!menuOpen) return
        const onDown = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
        }
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false) }
        document.addEventListener('mousedown', onDown)
        document.addEventListener('keydown', onKey)
        return () => {
            document.removeEventListener('mousedown', onDown)
            document.removeEventListener('keydown', onKey)
        }
    }, [menuOpen])

    const openTab = useCallback((spec: TabSpec) => {
        const existing = model.getNodeById(spec.id) as TabNode | undefined
        // Only re-select a tab that is actually attached to a tabset. An orphaned
        // leftover (in the id map but with no parent — from an earlier failed add)
        // would crash selectTab on `parent.getChildren()`. If we hit one, drop the
        // corrupt in-memory state and reload from the persisted JSON (which never
        // contains orphans) so the session self-heals with all real tabs intact.
        if (existing) {
            if (existing.getParent()) {
                if (existing.getName() !== spec.name) model.doAction(Actions.renameTab(spec.id, spec.name))
                model.doAction(Actions.selectTab(spec.id))
                return
            }
            // This reload throws away unsaved forms like any other — and it is the
            // one the user did not ask for, so it gets the same dialog rather than
            // a silent restart. (The layout itself survives: it is persisted first.)
            const healReload = () => {
                try { localStorage.setItem(LS_KEY, JSON.stringify(model.toJson())) } catch { /* ignore */ }
                window.location.reload()
            }
            if ([...guards.current.values()].some((g) => g.dirty)) {
                setBulkErrors([])
                setExitPrompt({ kind: 'reload', proceed: healReload })
                return
            }
            healReload()
            return
        }
        // Resolve a valid target tabset: the active one, else the first tabset in
        // the model. Never pass an id that isn't in the model — that would create
        // the new tab in the id map without attaching it (an orphan).
        let targetId = model.getActiveTabset()?.getId()
        if (!targetId) {
            let firstTabsetId: string | undefined
            model.visitNodes((n) => { if (!firstTabsetId && n.getType() === 'tabset') firstTabsetId = n.getId() })
            targetId = firstTabsetId
        }
        if (!targetId) return // no tabset at all (shouldn't happen)
        model.doAction(Actions.addNode(
            { type: 'tab', id: spec.id, name: spec.name, component: spec.component, config: spec.config },
            targetId, DockLocation.CENTER, -1,
        ))
    }, [model])

    const renameTab = useCallback((id: string, name: string) => {
        const node = model.getNodeById(id) as TabNode | undefined
        if (node?.getParent() && node.getName() !== name) model.doAction(Actions.renameTab(id, name))
    }, [model])

    const closeTab = useCallback((id: string) => {
        if (!model.getNodeById(id)) return
        // Programmatic close (e.g. after a successful save): clear the guard so
        // onAction below doesn't pop the "unsaved changes" dialog.
        const g = guards.current.get(id)
        if (g) guards.current.set(id, { ...g, dirty: false })
        model.doAction(Actions.deleteTab(id))
    }, [model])

    // Intercept user-initiated tab close (the × button). If the tab has unsaved
    // changes, cancel the close and open the save/discard/cancel dialog.
    const onAction = useCallback((action: Action): Action | undefined => {
        if (action.type === Actions.DELETE_TAB) {
            const tabId = action.data?.node as string
            if (guards.current.get(tabId)?.dirty) { setConfirmError(''); setConfirm(tabId); return undefined }
        }
        return action
    }, [])

    // A tab title carries a document name (invoice number, template, customer) and can run long
    // enough to push the other tabs out of reach. Cut it for display only — the full text stays
    // in the tooltip, and nothing else reads the shortened value.
    // Show the sidebar icon before the title, plus a ● when the tab is dirty.
    // dirtyTick is in the deps so a new identity is passed to <Layout> whenever a
    // guard's dirty state flips, forcing the tab strip to re-render.
    const onRenderTab = useCallback((node: TabNode, rv: ITabRenderValues) => {
        const icon = ICONS[COMPONENT_ICON[node.getComponent() ?? '']]
        const dirty = guards.current.get(node.getId())?.dirty
        rv.leading = (
            <span className="tab-leading">
                {icon && <span className="tab-icon">{icon}</span>}
                {dirty && <span className="tab-dirty-dot" aria-label={T.unsavedAria} />}
            </span>
        )
        const name = node.getName() ?? ''
        if (name.length > MAX_TAB_TITLE) {
            rv.content = <span title={name}>{`${name.slice(0, MAX_TAB_TITLE - 1).trimEnd()}…`}</span>
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dirtyTick])

    // Dialog: save then close. On failure keep the dialog open and show the error.
    const confirmSave = useCallback(async () => {
        if (!confirm) return
        const g = guards.current.get(confirm)
        if (!g) { setConfirm(null); return }
        setConfirmBusy(true); setConfirmError('')
        const res = await g.save().catch(() => ({ ok: false } as SaveResult))
        setConfirmBusy(false)
        if (res.ok) {
            const id = confirm
            setConfirm(null)
            if (model.getNodeById(id)) {
                guards.current.set(id, { ...g, dirty: false })
                model.doAction(Actions.deleteTab(id))
            }
        } else {
            setConfirmError(res.error ?? T.saveFailed)
        }
    }, [confirm, model])

    // Dialog: discard changes and close the tab.
    const confirmDiscard = useCallback(() => {
        if (!confirm) return
        const id = confirm
        const g = guards.current.get(id)
        if (g) guards.current.set(id, { ...g, dirty: false }) // bypass the re-prompt
        setConfirm(null)
        if (model.getNodeById(id)) model.doAction(Actions.deleteTab(id))
    }, [confirm, model])

    const notifyChange = useCallback((resource: string, id?: string) => {
        setVersions((v) => {
            const next = { ...v, [resource]: (v[resource] ?? 0) + 1 }
            if (id) next[`${resource}:${id}`] = (v[`${resource}:${id}`] ?? 0) + 1
            return next
        })
    }, [])

    // Save → notify other clients (socket) + refresh own lists.
    const publishChange = useCallback((resource: string, id?: string) => {
        getSocket().emit('data:changed', { resource, id })
        notifyChange(resource)
    }, [notifyChange])

    // Other clients' saves → refresh our lists + any open detail tab for that record.
    useEffect(() => {
        const s = getSocket()
        const onChanged = (p: { resource: string, id?: string }) => notifyChange(p.resource, p.id)
        s.on('data:changed', onChanged)
        // Back after a disconnect → what was sent in between is lost; refresh everything once.
        const onReconnect = () => notifyChange(ANY_RESOURCE)
        s.io.on('reconnect', onReconnect)
        return () => { s.off('data:changed', onChanged); s.io.off('reconnect', onReconnect) }
    }, [notifyChange])

    const factory = useCallback((node: TabNode) => {
        const component = node.getComponent() ?? ''
        const config = (node.getConfig() ?? {}) as { id?: string, url?: string, fname?: string, brand?: string, version?: string, q?: string, itemId?: number }
        const tabId = node.getId()
        // RBAC gate: a module a user may not read (e.g. from a restored layout)
        // shows a notice instead of hitting the API with a 403.
        const mod = COMPONENT_MODULE[component]
        if (mod && !canReadAny(canRef.current, mod)) {
            return <div className="tab-content"><p className="muted" style={{ padding: 24 }}>{T.noAccess}</p></div>
        }
        const page = (() => {
            switch (component) {
                case 'dashboard': return <Dashboard />
                case 'customers': return <CustomersList />
                case 'customer-form': return <CustomerForm tabId={tabId} id={config.id} />
                case 'invoices': return <InvoicesList />
                case 'invoice-form': return <InvoiceForm tabId={tabId} id={config.id} />
                case 'certificate-dispatch': return <CertificateDispatchList />
                case 'templates': return <Templates tabId={tabId} initialPill={(config as { pill?: TemplatesPill }).pill} />
                // A saved tab from the old Finanzen → PDF-Vorlagen entry.
                case 'invoice-templates': return <Templates tabId={tabId} initialPill="pdf" />
                case 'invoice-template-form': return <InvoiceTemplateEditor tabId={tabId} brand={config.brand ?? 'deja'} version={config.version} />
                case 'subscriptions': return <SubscriptionsList />
                case 'transactions': return <TransactionsList />
                case 'subscription-form': return <SubscriptionForm tabId={tabId} id={config.id} />
                case 'export': return <MonthlyExport />
                case 'settings': return <Settings tabId={tabId} />
                case 'webhooks': return <WebhooksManager />
                case 'logs': return <LogsViewer initialQuery={config.q} />
                case 'trash': return <TrashView />
                case 'users': return <UsersList />
                case 'user-form': return <UserForm tabId={tabId} id={config.id} />
                case 'roles': return <RolesList />
                case 'role-editor': return <RoleEditor tabId={tabId} id={config.id} />
                case 'expense-mapping': return <ExpenseMappingAdmin />
                case 'catalog': return <AuthorCatalog />
                case 'catalog-item-form': return <CatalogItemForm tabId={tabId} id={config.id} itemId={config.itemId} />
                case 'authors': return <AuthorsList />
                case 'author-form': return <AuthorForm tabId={tabId} id={config.id} />
                case 'author-statements': return <AuthorStatements />
                case 'vat-settings': return <VatSettings tabId={tabId} />
                // Self-service account page — intentionally not in COMPONENT_MODULE,
                // so it has no RBAC gate (every logged-in user may open it).
                case 'profile': return <Profile tabId={tabId} />
                // Inline document viewer (PDF/CSV…); the file URL itself is API-gated.
                case 'file-viewer': return <FileViewer url={config.url} fname={config.fname} />
                default: return <div>{fill(T.unknownComponent, { component })}</div>
            }
        })()
        return <div className="tab-content">{page}</div>
    }, [])

    const onModelChange = useCallback(() => {
        try { localStorage.setItem(LS_KEY, JSON.stringify(model.toJson())) } catch { /* ignore */ }
        setEmpty(countTabs(model) === 0)
    }, [model])

    const ctx = useMemo(
        () => ({ openTab, closeTab, renameTab, notifyChange, publishChange, versions, registerDirtyGuard, unregisterDirtyGuard }),
        [openTab, closeTab, renameTab, notifyChange, publishChange, versions, registerDirtyGuard, unregisterDirtyGuard],
    )

    // The sidebar is only for OPENING tabs — it deliberately never marks an item as
    // "active" (the open tabs themselves show what is selected).

    return (
        <TabsContext.Provider value={ctx}>
            <div className="shell">
                <header className="topbar">
                    <div className="topbar-brand">
                        <img src="/logo-deja.png" alt={T.logoAlt} className="topbar-logo" />
                        <span className="topbar-title">{T.appTitle}</span>
                    </div>
                    <div className="topbar-right">
                        {/* Only shown when something is actually unsaved — a permanent
                            badge would turn into wallpaper and stop being noticed. */}
                        {dirtyTabs.length > 0 && (
                            <div className="topbar-dirty" ref={dirtyRef}>
                                <button type="button" className="dirty-badge"
                                    aria-haspopup="menu" aria-expanded={dirtyListOpen}
                                    onClick={() => { setBulkErrors([]); setDirtyListOpen((o) => !o) }}>
                                    <span className="tab-dirty-dot" aria-hidden="true" />
                                    {fill(T.dirtyCount, { n: dirtyTabs.length })}
                                </button>
                                {dirtyListOpen && (
                                    <div className="dirty-pop" role="menu">
                                        <div className="dirty-pop-head">{T.dirtyListHead}</div>
                                        {dirtyTabs.map((t) => (
                                            <button key={t.id} type="button" role="menuitem"
                                                title={t.name}
                                                onClick={() => { setDirtyListOpen(false); model.doAction(Actions.selectTab(t.id)) }}>
                                                {t.name}
                                            </button>
                                        ))}
                                        {bulkErrors.length > 0 && (
                                            <div className="dirty-pop-errors">
                                                {bulkErrors.map((err) => <div key={err} className="notice err">{err}</div>)}
                                            </div>
                                        )}
                                        <div className="dirty-pop-foot">
                                            <button type="button" className="dirty-pop-save"
                                                onClick={saveAllFromList} disabled={bulkBusy}>
                                                {bulkBusy ? '…' : T.saveAll}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        <FullscreenButton />
                        <div className="topbar-menu" ref={menuRef}>
                            <button type="button" className="hamburger" aria-label={T.menuAria}
                                aria-haspopup="menu" aria-expanded={menuOpen}
                                onClick={() => setMenuOpen((o) => !o)}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
                                    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <line x1="3" y1="6" x2="21" y2="6" />
                                    <line x1="3" y1="12" x2="21" y2="12" />
                                    <line x1="3" y1="18" x2="21" y2="18" />
                                </svg>
                            </button>
                            {menuOpen && (
                                <div className="menu-dropdown" role="menu">
                                    {user && (
                                        <div className="menu-user">
                                            <div className="menu-user-name">{user.name}</div>
                                            <div className="menu-user-email">{user.email}</div>
                                        </div>
                                    )}
                                    <button type="button" role="menuitem"
                                        onClick={() => { setMenuOpen(false); openTab({ id: 'profile', name: T.myProfile, component: 'profile' }) }}>
                                        {T.myProfile}
                                    </button>
                                    <button type="button" role="menuitem"
                                        onClick={() => { setMenuOpen(false); requestLogout() }}>
                                        {T.logout}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </header>
                <div className="app">
                    <aside className="sidebar">
                        <nav>
                            {SIDEBAR.map((entry) => {
                                if (!isGroup(entry)) {
                                    if (!canReadAny(can, entry.module)) return null
                                    return (
                                        <button key={entry.id} type="button"
                                            className="navlink"
                                            title={entry.name} aria-label={entry.name}
                                            onClick={() => openTab(entry)}>
                                            {ICONS[entry.id]}<span className="navlink-label">{entry.name}</span>
                                        </button>
                                    )
                                }
                                const children = entry.children.filter((c) => canReadAny(can, c.module))
                                if (!children.length) return null
                                const open = openGroups[entry.id] ?? true
                                return (
                                    <div key={entry.id} className="navgroup">
                                        <button type="button" className="navgroup-header"
                                            aria-expanded={open} onClick={() => toggleGroup(entry.id)}>
                                            {ICONS[entry.id]}<span className="navlink-label">{entry.name}</span>
                                            <span className={`navgroup-caret${open ? ' open' : ''}`}>{CARET}</span>
                                        </button>
                                        {open && (
                                            <div className="navgroup-children">
                                                {children.map((c) => (
                                                    <button key={c.id} type="button"
                                                        className="navlink child"
                                                        title={c.name} aria-label={c.name}
                                                        onClick={() => openTab(c)}>
                                                        {ICONS[c.id]}<span className="navlink-label">{c.name}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </nav>
                        <div className="sidebar-version" title={fill(T.versionTitle, { version: __APP_VERSION__ })}>{fill(T.version, { version: __APP_VERSION__ })}</div>
                    </aside>
                    <div className="tab-area">
                        <Layout model={model} factory={factory} onModelChange={onModelChange}
                            onAction={onAction} onRenderTab={onRenderTab} />
                        {empty && (
                            <div className="empty-state">
                                <img src="/logo-deja.png" alt={T.logoAlt} className="empty-logo" />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {confirm && (
                <div className="modal-backdrop" onMouseDown={() => !confirmBusy && setConfirm(null)}>
                    <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
                        <h2>{T.closeDialog.title}</h2>
                        <p>{T.closeDialog.text}</p>
                        {confirmError && <div className="notice err">{confirmError}</div>}
                        <div className="modal-actions">
                            <button type="button" onClick={confirmSave} disabled={confirmBusy}>
                                {confirmBusy ? '…' : T.closeDialog.save}
                            </button>
                            <button type="button" className="danger" onClick={confirmDiscard} disabled={confirmBusy}>
                                {T.closeDialog.discard}
                            </button>
                            <button type="button" className="secondary" onClick={() => setConfirm(null)} disabled={confirmBusy}>
                                {T.closeDialog.cancel}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {exitPrompt && (
                <div className="modal-backdrop" onMouseDown={() => !bulkBusy && setExitPrompt(null)}>
                    <div className="modal modal-wide" onMouseDown={(e) => e.stopPropagation()}>
                        <h2>{T.exitDialog.title[exitPrompt.kind]}</h2>
                        <p>
                            {dirtyTabs.length === 1
                                ? T.exitDialog.textOne
                                : T.exitDialog.textMany}
                        </p>
                        <ul className="modal-list">
                            {dirtyTabs.map((t) => <li key={t.id}>{t.name}</li>)}
                        </ul>
                        {bulkErrors.map((err) => <div key={err} className="notice err">{err}</div>)}
                        <div className="modal-actions">
                            <button type="button" onClick={saveAllAndProceed} disabled={bulkBusy}>
                                {bulkBusy ? '…' : T.saveAll}
                            </button>
                            <button type="button" className="danger" onClick={discardAndProceed} disabled={bulkBusy}>
                                {T.exitDialog.discard[exitPrompt.kind]}
                            </button>
                            <button type="button" className="secondary" onClick={() => setExitPrompt(null)} disabled={bulkBusy}>
                                {T.closeDialog.cancel}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </TabsContext.Provider>
    )
}
